// kelly-1005-P1-portrait-video-length-before-upload: local-file video duration
// reading + plain-language cap messaging, shared by every UploadMedia instance
// that opts in via `videoMaxDurationSeconds` — not portrait-specific, so any
// surface reusing the shared picker (MediaSelection -> UploadMedia) gets the
// SAME instant, no-network "too long" check before an upload starts.
//
// Kelly, 2026-10-05: a 23s video uploaded fully to S3 before the user ever
// learned it was rejected, and the rejection then quoted a byte limit ("500
// MB") nobody can picture. The fix is to measure the file's OWN duration
// client-side (a hidden <video> + loadedmetadata, zero network calls) before
// any upload begins, and to talk in seconds, not bytes.

/**
 * Read a local video File's duration in seconds via a hidden <video> element.
 * Resolves `null` when the duration cannot be determined (corrupt file, a
 * container the browser can't decode, no DOM `URL.createObjectURL`) — callers
 * must treat `null` as "unknown", never as "passes" (same contract
 * utils/portraitMedia.js#validatePortraitVideoFile already uses for its
 * optional durationSeconds argument): an unreadable file is NOT blocked
 * client-side, it just proceeds to the normal upload/server path.
 *
 * @param {File} file
 * @returns {Promise<number|null>}
 */
export function readLocalVideoDurationSeconds(file) {
  return new Promise((resolve) => {
    if (!file || typeof file !== "object" || typeof URL === "undefined" || !URL.createObjectURL) {
      resolve(null);
      return;
    }
    let url;
    try {
      url = URL.createObjectURL(file);
    } catch (e) {
      resolve(null);
      return;
    }
    const video = document.createElement("video");
    video.preload = "metadata";
    let settled = false;
    const finish = (result) => {
      if (settled) return;
      settled = true;
      video.onloadedmetadata = null;
      video.onerror = null;
      URL.revokeObjectURL(url);
      resolve(result);
    };
    video.onloadedmetadata = () => {
      const d = video.duration;
      finish(Number.isFinite(d) && d > 0 ? d : null);
    };
    video.onerror = () => finish(null);
    video.src = url;
  });
}

const secondsWords = (seconds) => {
  const n = Math.round(seconds);
  return `${n} second${n === 1 ? "" : "s"}`;
};

/** The headline shown where people decide (drop zone / Record tab), e.g.
 * "Portrait videos up to 15 seconds" — plain words, never a byte count. */
export const videoDurationCapLabel = (maxSeconds, contextLabel = "Videos") =>
  `${contextLabel} up to ${secondsWords(maxSeconds)}`;

/** The rejection sentence, said BEFORE any upload is attempted. Names the
 * file's actual length so the user knows what to trim, never a raw byte
 * count. e.g. "This video is 23 seconds. Portrait videos can be up to 15
 * seconds. Please choose a shorter clip." */
export const videoTooLongMessage = (actualSeconds, maxSeconds, contextLabel = "Videos") =>
  `This video is ${secondsWords(actualSeconds)}. ${contextLabel} can be up to ` +
  `${secondsWords(maxSeconds)}. Please choose a shorter clip.`;

/** Plain-language size rejection — fine print only, paired with the duration
 * headline above it, never the sole explanation. */
export const videoTooLargeMessage = (maxBytes, contextLabel = "Videos") =>
  `${contextLabel} can be up to ${Math.round(maxBytes / (1024 * 1024))}MB.`;

// gordon-1005-P2-portrait-video-trimmer: Gordon, 2026-10-05 20:55 PT — a video
// over the cap should OFFER A TRIMMER, not only refuse. This replaces
// `videoTooLongMessage` as the copy shown when a too-long file is picked (that
// function stays exported — and unit-tested — for its own sake, but
// UploadMedia no longer toasts an outright rejection for this case).
/** The trim-offer sentence, said BEFORE any upload is attempted. Still names
 * both numbers (actual length, cap) like the rejection message it replaces,
 * but points at the trimmer instead of telling the user to pick a new file.
 * e.g. "This video is 23 seconds. Portrait videos can be up to 15 seconds.
 * Drag the window below to choose which 15 seconds to use." */
export const videoTrimInviteMessage = (actualSeconds, maxSeconds, contextLabel = "Videos") =>
  `This video is ${secondsWords(actualSeconds)}. ${contextLabel} can be up to ` +
  `${secondsWords(maxSeconds)}. Drag the window below to choose which ` +
  `${secondsWords(maxSeconds)} to use.`;
